import { CarfrontCvna } from '@carvana/ds-icons';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useBrandCapabilities, useBrandCopy } from '|/brand';
import useOrderSummaryVm from '|/components/carvana-ds/order-summary-display/use-order-summary-vm';
import currency from '|/helpers/currency';
import { ReviewSummaryResponse } from '|/services/purchase-api';
import { AmountRow, ExpanderRow } from './amount-row';
import PriceSection from './price-section';
import { PriceDetailsView } from '../derive-price-details';
import { deriveVehiclePriceRows, VehiclePriceRow } from '../derive-vehicle-price-rows';
import styles from '../price-details.module.css';

interface Incentive {
  description: string;
  amount: number;
  expirationDate?: string | null;
}

/* currency() renders Math.abs(value), so a negative total needs its sign put back by hand
   or it reads as a plausible positive one. */
const signedAmount = ({ amount, isDiscount }: VehiclePriceRow) =>
  `${isDiscount || amount < 0 ? '-' : ''}${currency(amount)}`;

interface VehiclePriceSectionProps {
  summary: ReviewSummaryResponse;
  view: PriceDetailsView;
}

const VehiclePriceSection = ({ summary, view }: VehiclePriceSectionProps) => {
  const {
    collapsedFees,
    setCollapsedFees,
    collapsedShipping,
    setCollapsedShipping,
    collapsedIncentives,
    setCollapsedIncentives,
    totalTaxes,
    totalFeesAmount,
    shippingFee,
    subtotal,
    msrp,
    vehicleStickerPrice,
    totalDiscountsAmount,
    totalIncentives,
    incentives,
    creditKarmaAmountSaved,
    tradeBoostAmountSaved,
    employeeDiscountAmount,
    feesList,
    accessories,
    selfInstalledOptionsShippingCost,
    nonFactoryInstalledAccessoriesShippingCost
  } = useOrderSummaryVm(summary);
  const { checkout } = useBrandCapabilities();
  const copy = useBrandCopy();

  /* Slate itemises the accessories it sells and bills their shipping separately. Local
     state rather than the shared vm's collapse flags — these two groups exist on this
     surface only. */
  const [collapsedAccessories, setCollapsedAccessories] = useState(false);
  const [collapsedAccessoriesShipping, setCollapsedAccessoriesShipping] = useState(false);
  const showAccessories = checkout.sellsAccessories && accessories.length > 0;
  const accessoriesTotalAmount = accessories.reduce(
    (total, accessory) => total + accessory.productPrice + accessory.installationPrice,
    0
  );

  const { rows, showIncentives, ruleBelowIncentives, netRow } = deriveVehiclePriceRows({
    msrp,
    vehicleStickerPrice,
    tradeBoostAmountSaved,
    creditKarmaAmountSaved,
    employeeDiscountAmount,
    priceReductions: totalDiscountsAmount,
    totalIncentives,
    sellsPreOwnedVehicles: checkout.sellsPreOwnedVehicles
  });

  return (
    <PriceSection
      icon={<CarfrontCvna size={16} color="var(--icon-strong)" />}
      title="Vehicle price"
      testId="pp_price-details_vehicle-price"
    >
      {rows.map(row => (
        <AmountRow
          key={row.id}
          label={row.label}
          amount={signedAmount(row)}
          emphasis={row.isDiscount ? 'discount' : 'default'}
          ruled={row.hasRuleBelow}
          testId={`pp_price-details_vehicle-price-${row.id}`}
        />
      ))}

      {showIncentives && (
        <ExpanderRow
          label="Manufacturer incentives"
          amount={`-${currency(totalIncentives ?? 0)}`}
          isOpen={!collapsedIncentives}
          onToggle={() => {
            setCollapsedIncentives(!collapsedIncentives);
          }}
          analyticsId="pp_review_order_summary_incentives_toggle"
          testId="pp_price-details_incentives-toggle"
          hideAmountWhenOpen
          ruled={ruleBelowIncentives}
        >
          {(incentives as Incentive[] | null)?.map(incentive => (
            <AmountRow
              key={incentive.description}
              label={
                <>
                  {incentive.description}
                  {incentive.expirationDate && (
                    <span className={styles.rowSubtext}>
                      Valid until {dayjs(incentive.expirationDate).format('MMMM D, YYYY')}
                    </span>
                  )}
                </>
              }
              amount={`-${currency(incentive.amount)}`}
              emphasis="muted"
            />
          ))}
        </ExpanderRow>
      )}

      {netRow && (
        <AmountRow
          label={netRow.label}
          amount={signedAmount(netRow)}
          emphasis="total"
          testId={`pp_price-details_vehicle-price-${netRow.id}`}
        />
      )}

      {showAccessories && (
        <ExpanderRow
          label="Slate accessories"
          amount={currency(accessoriesTotalAmount)}
          isOpen={!collapsedAccessories}
          onToggle={() => {
            setCollapsedAccessories(!collapsedAccessories);
          }}
          analyticsId="pp_review_order_summary_accessories_toggle"
          testId="pp_price-details_accessories-toggle"
          hideAmountWhenOpen
        >
          {accessories.map(accessory => (
            <React.Fragment key={accessory.name}>
              <AmountRow
                label={accessory.name}
                amount={currency(accessory.productPrice)}
                emphasis="muted"
              />
              {accessory.installationPrice > 0 && (
                <AmountRow
                  label={`${accessory.name} installation`}
                  amount={currency(accessory.installationPrice)}
                  emphasis="muted"
                />
              )}
            </React.Fragment>
          ))}
        </ExpanderRow>
      )}

      <div className={styles.taxGroup}>
        <AmountRow label="Sales tax" amount={currency(totalTaxes)} testId="sales-tax-row" />
        {view.showTaxSavings && (
          <p className={styles.savingsHighlight} data-testid="pp_price-details_tax-savings">
            {view.taxSavingsCopy}
          </p>
        )}
      </div>

      {view.showTitleAndRegistration && (
        <ExpanderRow
          label="Title and registration"
          amount={currency(totalFeesAmount)}
          isOpen={!collapsedFees}
          onToggle={() => {
            setCollapsedFees(!collapsedFees);
          }}
          analyticsId="pp_review_order_summary_fees_toggle"
          testId="pp_price-details_fees-toggle"
          hideAmountWhenOpen
        >
          {feesList?.map((fee: { description: string; amount: number }) => (
            <AmountRow
              key={fee.description}
              label={fee.description}
              amount={currency(fee.amount)}
              emphasis="muted"
            />
          ))}
        </ExpanderRow>
      )}

      {showAccessories && (
        <ExpanderRow
          label="Accessories shipping"
          amount={currency(
            selfInstalledOptionsShippingCost + nonFactoryInstalledAccessoriesShippingCost
          )}
          isOpen={!collapsedAccessoriesShipping}
          onToggle={() => {
            setCollapsedAccessoriesShipping(!collapsedAccessoriesShipping);
          }}
          analyticsId="pp_review_order_summary_accessories_shipping_toggle"
          testId="pp_price-details_accessories-shipping-toggle"
          hideAmountWhenOpen
        >
          <AmountRow
            label="Self-installed options"
            amount={currency(selfInstalledOptionsShippingCost)}
            emphasis="muted"
          />
          <AmountRow
            label="Additional Slate accessories"
            amount={currency(nonFactoryInstalledAccessoriesShippingCost)}
            emphasis="muted"
          />
        </ExpanderRow>
      )}

      {view.showShipping && (
        <ExpanderRow
          label={copy.nonrefundableShippingLabel}
          amount={currency(shippingFee ?? 0)}
          isOpen={!collapsedShipping}
          onToggle={() => {
            setCollapsedShipping(!collapsedShipping);
          }}
          analyticsId="pp_price_details_shipping_toggle"
          testId="pp_price-details_shipping-toggle"
        >
          <p className={styles.expanderCopy}>
            Transporting the vehicle to a Carvana location near you.
          </p>
        </ExpanderRow>
      )}

      {checkout.advertisesNoDealerFees && <AmountRow label="Dealer fees" amount="NEVER" />}

      <AmountRow label="Subtotal" amount={currency(subtotal)} emphasis="total" />
    </PriceSection>
  );
};

export default VehiclePriceSection;
